import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment, { Moment } from 'moment';
import { of } from 'rxjs/internal/observable/of';
import rfdc from 'rfdc';
import { environment } from '../../../../environments/environment';
import { DefenceZone } from '../../_models/diseases/defenceZone';
import { UIRiskDegree } from '../../../shared/dashboard-risk-degree/risk-degree';
import { Disease } from '../../_models/diseases/disease';
import { StoreModelService } from '../storeModel.service';
import { SM } from '../../../../assets/data/enum/storeModel.enum';
import { DefenceZoneCrop } from '../../_models/defenceZoneCrop';
import { CommodityService } from '../commodity.service';

@Injectable()
export class DefenceZoneService {
  private apiBaseUrl = environment.apiBaseUrl;
  private apiJobUrl = environment.apiJobUrl;
  private clone = rfdc();
  defenceZoneItemObj: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  listFetchInProgress$: Observable<DefenceZone[] | null> | null = null;
  list$: BehaviorSubject<DefenceZone[] | null> = new BehaviorSubject<DefenceZone[] | null>(null);
  commodityObjSelected: Subject<any> = new Subject<any>();
  range = ['low', 'medium_low', 'medium', 'medium_high', 'high'];
  today = moment().format('YYYY-MM-DD');
  legendDataList = [
    {
      id: 1,
      status: 'low',
      color: '#00B050',
    },
    {
      id: 2,
      status: 'medium_low',
      color: '#92D050',
    },
    {
      id: 3,
      status: 'medium',
      color: '#FFE699',
    },
    {
      id: 4,
      status: 'medium_high',
      color: '#FFC000',
    },
    {
      id: 5,
      status: 'high',
      color: '#FF0000',
    },
    {
      id: 6,
      status: 'null',
      color: '#cacaca',
    },
    {
      id: 7,
      status: 'no_susceptible_crop',
      color: '#727272',
    },
  ];

  feedbackLegendList = [
    {
      id: 1,
      status: 'low',
      color: '#00B050',
    },
    {
      id: 2,
      status: 'medium_low',
      color: '#92D050',
    },
    {
      id: 3,
      status: 'medium',
      color: '#FFE699',
    },
    {
      id: 4,
      status: 'medium_high',
      color: '#FFC000',
    },
    {
      id: 5,
      status: 'high',
      color: '#FF0000',
    },
  ];

  agrosatTenderLegendDataList = [
    {
      id: 1,
      status: 'low',
      color: '#00B050',
      value: this.translate.instant('diseases.level_risk.low', {}),
    },
    {
      id: 2,
      status: 'medium_low',
      color: '#92D050',
      value: this.translate.instant('diseases.level_risk.medium_low', {}),
    },
    {
      id: 3,
      status: 'medium',
      color: '#FFE699',
      value: this.translate.instant('diseases.level_risk.medium', {}),
    },
    {
      id: 4,
      status: 'high',
      color: '#FF0000',
      value: this.translate.instant('diseases.level_risk.high', {}),
    },
    {
      id: 6,
      status: 'null',
      color: '#cacaca',
      value: this.translate.instant('diseases.level_risk.zero', {}),
    },
    {
      id: 7,
      status: 'no_susceptible_crop',
      color: '#727272',
      value: this.translate.instant('diseases.level_risk.no_susceptible_crop', {}),
    },
  ];

  customLegendDataList = [
    {
      id: 1,
      status: 'low',
      color: '#00B050',
    },
    {
      id: 2,
      status: 'medium_low',
      color: '#92D050',
    },
    {
      id: 3,
      status: 'medium',
      color: '#FFE699',
    },
    {
      id: 4,
      status: 'high',
      color: '#FF0000',
    },
  ];

  basilLegendDataList = [
    {
      id: 1,
      status: 'zero', //nullo oppure basso chiedere
      color: '#FFFF00',
      value: this.translate.instant('diseases.level_risk.zero', {}),
      feedbackValue: 1,
    },
    {
      id: 2,
      status: 'medium_low',
      color: '#FFCC01',
      value: this.translate.instant('diseases.level_risk.medium_low', {}),
      feedbackValue: 4,
    },
    {
      id: 3,
      status: 'medium',
      color: '#FE9A01',
      value: this.translate.instant('diseases.level_risk.medium', {}),
      feedbackValue: 7,
    },
    {
      id: 4,
      status: 'medium_high',
      color: '#FF6600',
      value: this.translate.instant('diseases.level_risk.medium_high', {}),
      feedbackValue: 10,
    },
    {
      id: 5,
      status: 'high',
      color: '#FC3501',
      value: this.translate.instant('diseases.level_risk.high', {}),
      feedbackValue: 13,
    },
  ];

  diffusionLegendList = [
    {
      id: 1,
      status: 0,
      color: '#00B050',
    },
    {
      id: 2,
      status: 25,
      color: '#92D050',
    },
    {
      id: 3,
      status: 50,
      color: '#FFE699',
    },
    {
      id: 4,
      status: 75,
      color: '#FFC000',
    },
    {
      id: 5,
      status: 100,
      color: '#FF0000',
    },
  ];

  legendLeafBasil = [
    {
      status: 'zero',
      label: 'label_leaf_zero',
    },
    {
      status: 'medium_low',
      label: 'label_leaf_mediumlow',
    },
    {
      status: 'medium',
      label: 'label_leaf_medium',
    },
    {
      status: 'medium_high',
      label: 'label_leaf_mediumhigh',
    },
    {
      status: 'high',
      label: 'label_leaf_high',
    },
  ];

  cropsActivated: DefenceZoneCrop[] = [];
  fourScaleCommodity = [1528, 1823, 1862, 1840]; //tomato, tomato_2, barley, potato

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private storeModelService: StoreModelService,
    private commodityService: CommodityService
  ) {}

  initZoneObj() {
    this.defenceZoneItemObj = new BehaviorSubject<any>(null);
  }

  getActivatedCrops(): Observable<DefenceZoneCrop> {
    return this.http
      .get<DefenceZoneCrop>(`${this.apiJobUrl}api/public/v1/defence/supportedCommodities`)
      .pipe(
        tap((res) => {
          if (res) {
            this.cropsActivated = Array.isArray(res) ? res : [res];
          }
        })
      );
  }

  getCommodityObjSelected = () => {
    return this.commodityObjSelected.asObservable();
  };

  updateCommodityObjSelected(bool: boolean) {
    this.commodityObjSelected.next(bool);
  }

  getZone(id: number): Observable<DefenceZone> {
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v1/diseases/zones/' + id)
      .pipe(map((response) => response));
  }

  // it will get List and address store model and multi subscription
  getList(): Observable<DefenceZone[] | null> {
    const isAllowed = this.storeModelService.enableActionByStoreModelAndPermission(
      SM.SM_PATHOGEN
    ).read;

    if (!isAllowed) {
      return of(null);
    }

    if (this.listFetchInProgress$) {
      return this.listFetchInProgress$;
    }

    this.listFetchInProgress$ = this.http
      .get<DefenceZone[]>(this.apiBaseUrl + 'api/private/v1/diseases/zones')
      .pipe(
        tap((response) => this.list$.next(response)),
        map((res) => res),
        finalize(() => {
          this.listFetchInProgress$ = null; // reset after completion
        }),
        shareReplay(1)
      );

    return this.listFetchInProgress$;
  }

  getOrFetchList(): Observable<DefenceZone[] | null> {
    const currentValue = this.list$.getValue();

    if (currentValue && currentValue.length > 0) {
      return this.list$.asObservable();
    } else {
      return this.getList();
    }
  }

  getPathogensByZoneId(diseaseZoneId: number): Observable<any> {
    return this.http
      .get<any>(
        this.apiBaseUrl +
          'api/private/v1/diseases/externalPathogens' +
          '?diseaseZoneId=' +
          diseaseZoneId
      )
      .pipe(map((response) => response));
  }

  save(item): Observable<DefenceZone> {
    const data = { ...item };
    delete data.lastUpdated;
    delete data.dateCreated;

    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    data.companyId = companySelected ? companySelected.id : null;

    if (data.id) {
      return this.http
        .put<DefenceZone>(this.apiBaseUrl + 'api/private/v1/diseases/zones/' + data.id, data)
        .pipe(
          map((response: any) => {
            // Controlla il codice HTTP nella risposta
            if (response) {
              // Ritorna l'oggetto response
              return response;
            } else {
              // Altrimenti ritorna null
              return null;
            }
          }),
          catchError((error: any) => {
            // Gestisci l'errore
            console.error('Error in PUT request:', error);
            return throwError(error);
          })
        );
    } else {
      return this.http
        .post<DefenceZone>(this.apiBaseUrl + 'api/private/v2/diseases/zones', data)
        .pipe(
          map((response: any) => {
            // Controlla il codice HTTP nella risposta
            if (response) {
              // Ritorna l'oggetto response
              return response;
            } else {
              // Altrimenti ritorna null
              return null;
            }
          }),
          catchError((error: any) => {
            // Gestisci l'errore
            console.error('Error in POST request:', error);
            return throwError(error);
          })
        );
    }
  }

  delete(data): Observable<DefenceZone> {
    return this.http
      .delete<void>(this.apiBaseUrl + 'api/private/v1/diseases/zones/' + data.id)
      .pipe(
        map(() => {
          // Ritorna un oggetto DefenceZone con l'ID
          return { id: data.id } as DefenceZone;
        }),
        catchError((error: any) => {
          // Gestisci l'errore
          console.error('Error in DELETE request:', error);
          return throwError(error);
        })
      );
  }

  getAttackProbabilityIndex = (risk, protection, externalSource, commodity = null): any => {
    if (risk === -2) {
      return this.legendDataList[6]; // crop cycle end
    }
    if (commodity && this.fourScaleCommodity.includes(commodity.id)) {
      if (risk > 0) {
        risk++;
      }
    }
    if (protection < 0) {
      if (risk < 0) {
        return this.legendDataList[5];
      } else if (risk === 0) {
        return this.legendDataList[0];
      } else if (risk === 1) {
        return this.legendDataList[0];
      } else if (risk === 2) {
        return this.legendDataList[2];
      } else if (risk === 3) {
        if (externalSource && externalSource === 'AGROSAT') {
          return this.legendDataList[4];
        } else {
          return this.legendDataList[3];
        }
      } else if (risk === 4) {
        return this.legendDataList[4];
      }
    } else if (protection >= 0 && protection < 20) {
      if (risk < 0) {
        return this.legendDataList[5];
      } else if (risk === 0) {
        return this.legendDataList[0];
      } else if (risk === 1) {
        return this.legendDataList[1];
      } else if (risk === 2) {
        return this.legendDataList[2];
      } else if (risk === 3) {
        if (externalSource && externalSource === 'AGROSAT') {
          return this.legendDataList[4];
        } else {
          return this.legendDataList[3];
        }
      } else if (risk === 4) {
        return this.legendDataList[4];
      }
    } else if (protection >= 20 && protection < 40) {
      if (risk < 0) {
        return this.legendDataList[5];
      } else if (risk === 0) {
        return this.legendDataList[0];
      } else if (risk === 1) {
        return this.legendDataList[1];
      } else if (risk === 2) {
        return this.legendDataList[2];
      } else if (risk === 3) {
        if (externalSource && externalSource === 'AGROSAT') {
          return this.legendDataList[4];
        } else {
          return this.legendDataList[3];
        }
      } else if (risk === 4) {
        return this.legendDataList[4];
      }
    } else if (protection >= 40 && protection < 60) {
      if (risk < 0) {
        return this.legendDataList[5];
      } else if (risk === 0) {
        return this.legendDataList[0];
      } else if (risk === 1) {
        return this.legendDataList[0];
      } else if (risk === 2) {
        return this.legendDataList[2];
      } else if (risk === 3) {
        if (externalSource && externalSource === 'AGROSAT') {
          return this.legendDataList[4];
        } else {
          return this.legendDataList[3];
        }
      } else if (risk === 4) {
        return this.legendDataList[4];
      }
    } else if (protection >= 60 && protection < 80) {
      if (risk < 0) {
        return this.legendDataList[5];
      } else if (risk === 0) {
        return this.legendDataList[0];
      } else if (risk === 1) {
        return this.legendDataList[0];
      } else if (risk === 2) {
        return this.legendDataList[1];
      } else if (risk === 3) {
        return this.legendDataList[2];
      } else if (risk === 4) {
        return this.legendDataList[2];
      }
    } else if (protection >= 80 && protection <= 100) {
      if (risk < 0) {
        return this.legendDataList[5];
      } else if (risk === 0) {
        return this.legendDataList[0];
      } else if (risk === 1) {
        return this.legendDataList[0];
      } else if (risk === 2) {
        return this.legendDataList[0];
      } else if (risk === 3) {
        return this.legendDataList[1];
      } else if (risk === 4) {
        return this.legendDataList[1];
      }
    }
  };

  getColorAndStatusByRiskIndex = (index, externalSource, commodity): Array<string> => {
    // tslint:disable-next-line:prefer-const
    const result = [];

    if (commodity && commodity.id == 1866 && externalSource == 'AGROSAT') {
      if (index >= 0 && index <= 2) {
        result[0] = '#FFFF00';
        result[1] = this.basilLegendDataList[0]; //basso
      } else if (index >= 3 && index <= 5) {
        result[0] = '#FFCC01';
        result[1] = this.basilLegendDataList[1]; //medio-basso
      } else if (index >= 6 && index <= 8) {
        result[0] = '#FE9A01';
        result[1] = this.basilLegendDataList[2]; //medio
      } else if (index >= 9 && index <= 12) {
        result[0] = '#FF6600';
        result[1] = this.basilLegendDataList[3]; //medio-alto
      } else if (index > 12) {
        result[0] = '#FC3501';
        result[1] = this.basilLegendDataList[4]; //alto
      }
    } else {
      if (index == -2) {
        result[0] = '#727272';
        result[1] = this.legendDataList[6]; //crop cycle end
        return result;
      }
      if (commodity && this.fourScaleCommodity.includes(commodity.id)) {
        if (index === 0) {
          result[0] = '#00B050';
          result[1] = this.legendDataList[0]; //basso
        } else if (index === 1) {
          result[0] = '#ffe699';
          result[1] = this.legendDataList[2]; //medio
        } else if (index === 2) {
          result[0] = '#ffc000';
          result[1] = this.legendDataList[3]; //medio alto
        } else if (index === 3) {
          result[0] = '#ff0000';
          result[1] = this.legendDataList[4]; //alto
        } else {
          result[0] = '#cacaca';
          result[1] = this.legendDataList[5];
        }
        return result;
      }
      if (index === 0) {
        result[0] = '#00B050';
        result[1] = this.legendDataList[0]; //basso
      } else if (index === 1) {
        result[0] = '#92d050';
        result[1] = this.legendDataList[1]; //medio-basso
      } else if (index === 2) {
        result[0] = '#ffe699';
        result[1] = this.legendDataList[2]; //medio
      } else if (index === 3) {
        if (externalSource && externalSource === 'AGROSAT') {
          result[0] = '#ff0000';
          result[1] = this.legendDataList[4]; //alto
        } else {
          result[0] = '#ffc000';
          result[1] = this.legendDataList[3]; //medio alto
        }
      } else if (index === 4) {
        result[0] = '#ff0000';
        result[1] = this.legendDataList[4]; //alto
      } else {
        result[0] = '#cacaca';
        result[1] = '#cacaca';
      }
    }
    return result;
  };

  // rischio differente per il patogeno NO (Nottua)
  getColorAndStatusByNottuaRiskIndex = (index): Array<string> => {
    // tslint:disable-next-line:prefer-const
    const result = [];
    if (index == -2) {
      result[0] = '#727272';
      result[1] = this.legendDataList[6]; //crop cycle end
      return result;
    }
    if (index === 0) {
      result[0] = '#00b050';
      result[1] = this.legendDataList[0];
    } else if (index === 1) {
      result[0] = '#92d050';
      result[1] = this.legendDataList[1];
    } else if (index === 2) {
      result[0] = '#ffe699';
      result[1] = this.legendDataList[2];
    } else if (index === 3) {
      result[0] = '#ffc000';
      result[1] = this.legendDataList[3];
    } else if (index >= 4) {
      result[0] = '#ff0000';
      result[1] = this.legendDataList[4];
    } else {
      result[0] = '#cacaca';
      result[1] = '#cacaca';
    }
    return result;
  };

  getColorAndStatusByProtectionIndex = (index): Array<string> => {
    // tslint:disable-next-line:prefer-const
    const result = [];
    if (index < 0) {
      result[0] = '#cacaca';
      result[1] = '#cacaca';
    } else if (index >= 0 && index < 20) {
      result[0] = '#f62f00';
      result[1] = this.legendDataList[0];
    } else if (index >= 20 && index < 40) {
      result[0] = '#f9c003';
      result[1] = this.legendDataList[1];
    } else if (index >= 40 && index < 60) {
      result[0] = '#fce699';
      result[1] = this.legendDataList[2];
    } else if (index >= 60 && index < 80) {
      result[0] = '#92d14f';
      result[1] = this.legendDataList[3];
    } else if (index >= 80 && index <= 100) {
      result[0] = '#00b050';
      result[1] = this.legendDataList[4];
    } else {
      result[0] = '#cacaca';
      result[1] = '#cacaca';
    }
    return result;
  };

  getRiskDegreeListByZoneList = (zoneList: Array<DefenceZone>, filterDay) => {
    // tslint:disable-next-line:prefer-const
    const arraylist = [];
    zoneList.forEach((zone) => {
      if (zone && zone.diseases && zone.diseases.length > 0) {
        if (zone.externalSource == 'AGROSAT' && zone.commodity.id == 1866) {
          const tmpDiseases = this.clone(zone.diseases);
          zone.diseases = (tmpDiseases || []).filter((disease) => disease.companyId !== -1);
        }
        let diseasesZoneFiltered: any = zone.diseases?.sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );
        const zoneExternalSource = zone.externalSource;
        if (filterDay === 'today') {
          diseasesZoneFiltered = zone.diseases.filter(
            (item) => moment(item.date).format('YYYY-MM-DD') === this.today
          );
        }

        const commodityZone = this.commodityService.getCommodityNameById(zone.commodity.id) || null;
        diseasesZoneFiltered.forEach((disease) => {
          // tslint:disable-next-line:prefer-const

          const grouped = disease.indexes.reduce(
            (acc, item) => {
              const key = item.pathogen;
              if (!acc[key]) {
                acc[key] = [];
              }
              acc[key].push(item);
              return acc;
            },
            {} as Record<string, typeof disease.indexes>
          );

          const groupByPathogen = Object.entries(grouped).map(([key, value]) => ({
            pathogen: key,
            indexes: value,
          }));
          groupByPathogen.forEach((item) => {
            // tslint:disable-next-line:prefer-const
            const riskDegreeItem = new UIRiskDegree();
            riskDegreeItem.zoneId = zone.id;
            riskDegreeItem.zoneName = zone.name;
            riskDegreeItem.externalSource = zone.externalSource;
            riskDegreeItem.pathogenName = item.pathogen;
            riskDegreeItem.pathogenNameTranslated = this.getNameTranslatedByPathogenNameCode(
              item.pathogen,
              commodityZone
            );
            riskDegreeItem.date = disease.date;
            riskDegreeItem.risk = item && item.indexes[0].risk ? item.indexes[0].risk : 0;

            if (
              item &&
              item.indexes[0].hasOwnProperty('primaryRisk') &&
              item.indexes[0].hasOwnProperty('secondaryRisk')
            ) {
              riskDegreeItem.primaryRisk = item.indexes[0].primaryRisk;
              riskDegreeItem.secondaryRisk = item.indexes[0].secondaryRisk;
              if (riskDegreeItem.primaryRisk < 0) {
                riskDegreeItem.primaryRisk = null;
              }
              if (riskDegreeItem.secondaryRisk < 0) {
                riskDegreeItem.secondaryRisk = null;
              }
              riskDegreeItem.riskType = 'primary-secondary';
            }

            if (
              item &&
              item.indexes[0].hasOwnProperty('germination') &&
              item.indexes[0].hasOwnProperty('infection')
            ) {
              riskDegreeItem.primaryRisk = item.indexes[0].germination;
              riskDegreeItem.secondaryRisk = item.indexes[0].infection;
              if (riskDegreeItem.primaryRisk < 0) {
                riskDegreeItem.primaryRisk = null;
              }
              if (riskDegreeItem.secondaryRisk < 0) {
                riskDegreeItem.secondaryRisk = null;
              }
              riskDegreeItem.riskType = 'germination-infection';
            } else if (item && item.indexes[0].hasOwnProperty('infection')) {
              riskDegreeItem.risk = item.indexes[0].infection;
              if (riskDegreeItem.risk < 0) {
                riskDegreeItem.risk = null;
              }
              riskDegreeItem.riskType = 'infection';
            }

            if (
              item &&
              item.indexes[0].hasOwnProperty('infection') &&
              item.indexes[0].hasOwnProperty('symptom')
            ) {
              riskDegreeItem.primaryRisk = item.indexes[0].infection;
              riskDegreeItem.secondaryRisk = item.indexes[0].symptom;
              if (riskDegreeItem.primaryRisk < 0) {
                riskDegreeItem.primaryRisk = null;
              }
              if (riskDegreeItem.secondaryRisk < 0) {
                riskDegreeItem.secondaryRisk = null;
              }
              riskDegreeItem.riskType = 'infection-symptom';
            }

            if (zone.externalSource == 'AGROSAT' && zone.commodity.id == 1866) {
              let riskResult = null;
              riskResult = this.getColorAndStatusByRiskIndex(
                riskDegreeItem.risk,
                zoneExternalSource,
                zone.commodity
              );
              riskDegreeItem.riskColor = riskResult[0];
              riskDegreeItem.identification = disease.id;
              riskDegreeItem.riskStatus = riskResult[1].status;
              riskDegreeItem.consistency = disease.consistency;

              let riskResultP = null;
              let riskResultS = null;

              riskResultP = this.getColorAndStatusByRiskIndex(
                riskDegreeItem.primaryRisk,
                zoneExternalSource,
                zone.commodity
              );
              riskResultS = this.getColorAndStatusByRiskIndex(
                riskDegreeItem.secondaryRisk,
                zoneExternalSource,
                zone.commodity
              );

              riskDegreeItem.riskColorPrimary = riskResultP[0];
              riskDegreeItem.riskStatusPrimary = riskResultP[1].status;
              riskDegreeItem.riskColorSecondary = riskResultS[0];
              riskDegreeItem.riskStatusSecondary = riskResultS[1].status;

              riskDegreeItem.consistency = disease.consistency;
            } else {
              riskDegreeItem.protection =
                item && item.indexes[0].protection ? item.indexes[0].protection : -1;
              if (item && item.indexes[0].hasOwnProperty('protectionTreatments')) {
                const treatments = item.indexes[0]?.protectionTreatments || [];
                const maxEfficency = treatments.reduce((max, current) => {
                  return !max || current.efficency > max.efficency ? current : max;
                }, undefined);
                if (maxEfficency != null) {
                  riskDegreeItem.protection = Math.round((<any>maxEfficency).efficency * 100);
                }
                riskDegreeItem.protectionTreatments = item.indexes[0].protectionTreatments;
              }
              // tslint:disable-next-line:prefer-const
              let riskResult = null;
              let riskResultP = null;
              let riskResultS = null;
              if (item.pathogen === 'NO') {
                riskResult = this.getColorAndStatusByNottuaRiskIndex(riskDegreeItem.risk);
                riskResultP = this.getColorAndStatusByNottuaRiskIndex(riskDegreeItem.primaryRisk);
                riskResultS = this.getColorAndStatusByNottuaRiskIndex(riskDegreeItem.secondaryRisk);
              } else {
                riskResult = this.getColorAndStatusByRiskIndex(
                  riskDegreeItem.risk,
                  zoneExternalSource,
                  zone.commodity
                );
                riskResultP = this.getColorAndStatusByRiskIndex(
                  riskDegreeItem.primaryRisk,
                  zoneExternalSource,
                  zone.commodity
                );
                riskResultS = this.getColorAndStatusByRiskIndex(
                  riskDegreeItem.secondaryRisk,
                  zoneExternalSource,
                  zone.commodity
                );
              }
              riskDegreeItem.riskColor = riskResult[0];
              riskDegreeItem.identification = disease.id;
              riskDegreeItem.riskStatus = riskResult[1].status;
              riskDegreeItem.riskColorPrimary = riskResultP[0];
              riskDegreeItem.riskStatusPrimary = riskResultP[1].status;
              riskDegreeItem.riskColorSecondary = riskResultS[0];
              riskDegreeItem.riskStatusSecondary = riskResultS[1].status;
              // tslint:disable-next-line:prefer-const
              let protectionResult = null;
              protectionResult = this.getColorAndStatusByProtectionIndex(riskDegreeItem.protection);
              riskDegreeItem.protectionColor = protectionResult[0];
              riskDegreeItem.protectionStatus = protectionResult[1].status;

              // tslint:disable-next-line:prefer-const
              const probabilityIndex = this.getAttackProbabilityIndex(
                riskDegreeItem.risk,
                riskDegreeItem.protection,
                zoneExternalSource
              );
              if (probabilityIndex && probabilityIndex.status && probabilityIndex.id) {
                riskDegreeItem.probabilityOfAttack = probabilityIndex.status;
                riskDegreeItem.probabilityOfAttackIndex = probabilityIndex.id;
              }
              let baseRiskToCalculateProb = 0;
              if (riskDegreeItem.riskType == 'primary-secondary') {
                baseRiskToCalculateProb =
                  riskDegreeItem.primaryRisk >= riskDegreeItem.secondaryRisk
                    ? riskDegreeItem.primaryRisk
                    : riskDegreeItem.secondaryRisk;
              }
              if (riskDegreeItem.riskType == 'germination-infection') {
                baseRiskToCalculateProb = riskDegreeItem.secondaryRisk;
              }
              if (riskDegreeItem.riskType == 'infection-symptom') {
                baseRiskToCalculateProb = riskDegreeItem.primaryRisk;
              }
              if (riskDegreeItem.riskType == 'infection') {
                baseRiskToCalculateProb = riskDegreeItem.risk;
              }

              const probabilityIndexP = this.getAttackProbabilityIndex(
                baseRiskToCalculateProb,
                riskDegreeItem.protection,
                zoneExternalSource,
                zone.commodity
              );
              if (probabilityIndexP && probabilityIndexP.status && probabilityIndexP.id) {
                riskDegreeItem.probabilityOfAttackPrimary = probabilityIndexP.status;
                riskDegreeItem.probabilityOfAttackIndexPrimary = probabilityIndexP.id;
              }
            }
            // fieldActivity By PathogenName
            if (zone.activitiesByPathogen && zone.activitiesByPathogen[item.pathogen]) {
              riskDegreeItem.fieldActivities = zone.activitiesByPathogen[item.pathogen];
            }

            arraylist.push(riskDegreeItem);
          });
        });
      }
    });
    return arraylist;
  };

  getColorByZoneIdAndPathogenName = (
    riskDegreeList: Array<UIRiskDegree>,
    zoneId: number,
    pathogenName: string,
    externalSouce?: string,
    crop?: any
  ) => {
    const riskDegree: UIRiskDegree | undefined = riskDegreeList.find(
      (x) =>
        x?.zoneId === zoneId &&
        x?.pathogenName === pathogenName &&
        moment(x?.date).format('YYYY-MM-DD') === this.today
    );

    let legendList = [];
    let filterLegend;
    if (riskDegree) {
      // tslint:disable-next-line:prefer-const
      if (
        zoneId != -1 &&
        externalSouce == 'AGROSAT' &&
        crop &&
        crop.commodity &&
        crop.commodity.id == 1866
      ) {
        legendList = this.basilLegendDataList;
        filterLegend = legendList.filter((item) => item.status === riskDegree.riskStatus);
      } else if (
        externalSouce == 'AGROSAT' &&
        crop &&
        crop.commodity &&
        crop.commodity.id == 1803
      ) {
        legendList = this.agrosatTenderLegendDataList;
        filterLegend = legendList.filter((item) => item.status === riskDegree.riskStatus);
      } else {
        legendList = this.legendDataList;
        filterLegend = legendList.filter((item) => item.status === riskDegree.probabilityOfAttack);
      }

      if (['primary-secondary', 'germination-infection'].includes(riskDegree.riskType)) {
        filterLegend = legendList.filter(
          (item) => item.status === riskDegree.probabilityOfAttackPrimary
        );
      }
      if (filterLegend.length > 0) {
        return filterLegend[0].color;
      } else {
        return '#000000';
      }
    } else {
      return '#000000';
    }
  };

  riskDegreeGroupByPathogenName = (riskDegreeList: Array<UIRiskDegree>) => {
    const grouped: Record<string, UIRiskDegree[]> = riskDegreeList.reduce((acc, item) => {
      const key = item.pathogenName;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {});

    return Object.entries(grouped).map(([key, value]) => ({
      pathogen: key,
      list: value,
    }));
  };

  getMeteoData(type, device, from, to): Observable<any[]> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    // tslint:disable-next-line:prefer-const
    const companyId = companySelected ? companySelected.id : null;

    // tslint:disable-next-line:max-line-length
    return this.http
      .get<any>(
        this.apiBaseUrl +
          'api/private/v1/meteo/' +
          type +
          '?farmId=' +
          companyId +
          '&deviceId=' +
          device +
          '&from=' +
          from +
          '&to=' +
          to +
          '&providerName'
      )
      .pipe(map((response: any) => response));
  }

  getNameTranslatedByPathogenNameCode = (pathogenName: string, commodityZone?: string): string => {
    // Build the dynamic translation key using pathogen name and commodity zone (or "default" if missing)
    const key = `pathogens.objects.${pathogenName}.${commodityZone || 'default'}`;

    // Try to resolve the translation for the specific key
    const translated = this.translate.instant(key);

    // If the translation exists (different from the raw key), return it
    if (translated && translated !== key) {
      return translated;
    }

    // Otherwise, try the fallback key using "default"
    const fallbackKey = `pathogens.objects.${pathogenName}.default`;
    const fallback = this.translate.instant(fallbackKey);

    // Return the fallback if available, otherwise null
    return fallback !== fallbackKey ? fallback : null;
  };

  getPhenologyNameApp4Agri(diseases: Array<Disease>): string {
    const tmpPhenology = diseases.find((x) => moment(x?.date).format('YYYY-MM-DD') === this.today);

    const phenologyTMP = tmpPhenology?.phenology;
    return phenologyTMP && phenologyTMP['CC'] != null ? phenologyTMP['CC'] + ' %' : '';
  }

  getPhenologyNameAgrosat(diseases: Array<Disease>): string | null {
    const tmpPhenology = diseases.find((x) => moment(x?.date).format('YYYY-MM-DD') === this.today);

    const phenologyTMP = tmpPhenology?.phenology;

    const phenologyList = phenologyTMP
      ? Object.entries(phenologyTMP).map(([prop, value]) => ({ prop, value }))
      : [];

    if (phenologyList.length > 0) {
      return this.capitalizeFirstLetter(phenologyList[0].prop.toLowerCase());
    }

    return null;
  }

  capitalizeFirstLetter(inputString: string) {
    return inputString.charAt(0).toUpperCase() + inputString.slice(1);
  }

  getRiskByListByDate = (list: any[], date: string | Date | Moment): any => {
    const formattedDate = moment(date).format('YYYY-MM-DD');

    const todayRisk = list.filter((x) => moment(x?.date).format('YYYY-MM-DD') === formattedDate);

    return todayRisk.length > 0 ? todayRisk[0] : null;
  };

  updatePhenologicalState(body): Observable<any> {
    return this.http
      .put(this.apiBaseUrl + 'api/private/v1/diseases/phenologyEvaluation/' + body.zone, body)
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error: any) => {
          // Gestisci l'errore
          console.error('Error in PUT request:', error);
          return throwError(error);
        })
      );
  }

  updateRiskDegreePathogen(body): Observable<any> {
    return this.http
      .put(this.apiBaseUrl + 'api/private/v1/diseases/pathogenEvaluation/' + body.zone, body)
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error: any) => {
          console.error('Error in PUT request:', error);
          return throwError(error);
        })
      );
  }
}
